import type {ReactNode} from 'react';
import {
  Box,
  Card,
  Divider,
  Flex,
  Group,
  Stack,
  Text,
  ThemeIcon,
  Title,
} from '@mantine/core';
import React from 'react';

/**
 * Card for displaying structured read-only metadata as labeled rows, with a
 * compact inline-label mode and optional section grouping.
 * @registry Card of labeled key/value rows, standard or compact layout.
 *
 * Ported from `library-m/data-display/InfoCard`. Prop surface kept identical.
 * Compound API (`InfoCard.Row`, `InfoCard.Section`) preserved as-is.
 *
 * Usage:
 * ```tsx
 * // Standard layout (stacked label/value)
 * <InfoCard title="Details" icon={<IconInfoCircle size={14} />}>
 *   <InfoCard.Row label="ID" value="12345" />
 *   <InfoCard.Row label="Status" value={<Badge>Active</Badge>} />
 * </InfoCard>
 *
 * // Compact layout (inline label: value)
 * <InfoCard title="Details" compact>
 *   <InfoCard.Row label="ID" value="12345" />
 *   <InfoCard.Row label="Status" value="Active" />
 * </InfoCard>
 * ```
 */

export interface InfoCardProps {
  title: string;
  icon?: ReactNode;
  iconColor?: string;
  children: ReactNode;
  /** Add visual separation between rows */
  dividers?: boolean;
  /** Compact mode: inline label/value instead of stacked */
  compact?: boolean;
  /** Right-aligned content in the title row (e.g. an edit action) */
  rightSection?: ReactNode;
}

export interface InfoCardRowProps {
  label: string;
  value: ReactNode;
  icon?: ReactNode;
  iconColor?: string;
}

export interface InfoCardSectionProps {
  title?: string;
  children: ReactNode;
}

// Context to pass compact mode to children
const InfoCardContext = React.createContext<{compact: boolean}>({
  compact: false,
});

// --- Sub-components ---

const InfoCardRow = ({
  label,
  value,
  icon,
  iconColor = 'gray',
}: InfoCardRowProps) => {
  const {compact} = React.useContext(InfoCardContext);

  if (compact) {
    // Compact: inline layout with label: value
    return (
      <Flex gap="xs" wrap="nowrap" align="center">
        {icon && (
          <ThemeIcon size="xs" variant="light" color={iconColor}>
            {icon}
          </ThemeIcon>
        )}
        <Text size="sm" c="dimmed" style={{flexShrink: 0}}>
          {label}:
        </Text>
        <Text size="sm" fw={500} style={{wordBreak: 'break-word'}}>
          {value ?? (
            <Text span c="dimmed">
              —
            </Text>
          )}
        </Text>
      </Flex>
    );
  }

  // Standard: stacked layout
  return (
    <Group gap="sm" wrap="nowrap" align="flex-start">
      {icon && (
        <ThemeIcon size="sm" variant="light" color={iconColor}>
          {icon}
        </ThemeIcon>
      )}
      <Box style={{flex: 1, minWidth: 0}}>
        <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
          {label}
        </Text>
        <Text size="sm" fw={500} style={{wordBreak: 'break-word'}}>
          {value ?? (
            <Text span c="dimmed">
              —
            </Text>
          )}
        </Text>
      </Box>
    </Group>
  );
};

const InfoCardSection = ({title, children}: InfoCardSectionProps) => (
  <Box>
    {title && (
      <Text size="xs" c="dimmed" tt="uppercase" fw={600} mb="xs">
        {title}
      </Text>
    )}
    {children}
  </Box>
);

// --- Main Component ---

const InfoCardRoot = ({
  title,
  icon,
  iconColor = 'blue',
  children,
  dividers = true,
  compact = false,
  rightSection,
}: InfoCardProps) => {
  // Process children to add dividers between rows (only in non-compact mode with dividers enabled)
  const childArray = React.Children.toArray(children);
  const showDividers = dividers && !compact;
  const processedChildren = showDividers
    ? childArray.flatMap((child, index) => {
        if (index === childArray.length - 1) return [child];
        return [child, <Divider key={`divider-${index}`} />];
      })
    : childArray;

  return (
    <InfoCardContext.Provider value={{compact}}>
      <Card shadow="sm" withBorder p={compact ? 'sm' : 'lg'}>
        <Flex align="center" justify="space-between" gap="sm" mb={compact ? 'xs' : 'md'}>
          <Group gap="xs">
            {icon && (
              <ThemeIcon size="sm" variant="light" color={iconColor}>
                {icon}
              </ThemeIcon>
            )}
            <Title order={compact ? 6 : 5}>{title}</Title>
          </Group>
          {rightSection}
        </Flex>
        <Stack gap={compact ? 'xs' : 'sm'}>{processedChildren}</Stack>
      </Card>
    </InfoCardContext.Provider>
  );
};

// --- Compound Component Export ---

export const InfoCard = Object.assign(InfoCardRoot, {
  Row: InfoCardRow,
  Section: InfoCardSection,
});

// Also export individual components
export {InfoCardRow, InfoCardSection};
